import { useEffect, useMemo, useRef, useState } from "react";
import type { OpenProjectBalanceAdjustmentPreviewResponse } from "../../types/openproject.js";
import type { BalanceCheckKey } from "./balanceResolutionModel.js";

type Preview = OpenProjectBalanceAdjustmentPreviewResponse;
type Request = {
  requestKey: string | null;
  checkKey: BalanceCheckKey;
  amount: number | null;
  unit: "hours" | "days";
  onPreview: (
    key: BalanceCheckKey,
    amount: number,
    unit: "hours" | "days",
  ) => Promise<Preview>;
};
type Result = {
  request: { key: string | null };
  preview: Preview | null;
  error: string | null;
};

/** A preview is usable only for the exact current source evidence and input. */
export function useBalanceAdjustmentPreview({
  requestKey,
  checkKey,
  amount,
  unit,
  onPreview,
}: Request) {
  const identity = useMemo(() => ({ key: requestKey }), [requestKey]);
  const latestRequest = useRef({ checkKey, amount, unit, onPreview });
  const [result, setResult] = useState<Result | null>(null);
  useEffect(() => {
    latestRequest.current = { checkKey, amount, unit, onPreview };
  }, [checkKey, amount, unit, onPreview]);
  useEffect(() => {
    if (requestKey === null) return;
    const controller = new AbortController();
    const load = async () => {
      const request = latestRequest.current;
      if (request.amount === null) return;
      try {
        const preview = await request.onPreview(
          request.checkKey,
          request.amount,
          request.unit,
        );
        if (!controller.signal.aborted)
          setResult({ request: identity, preview, error: null });
      } catch (cause) {
        if (!controller.signal.aborted)
          setResult({
            request: identity,
            preview: null,
            error:
              cause instanceof Error
                ? cause.message
                : "Could not calculate the adjustment preview.",
          });
      }
    };
    const timer = window.setTimeout(() => {
      void load();
    }, 220);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [requestKey, identity]);
  // Comparing during render withdraws eligibility before any effect/debounce runs.
  const current =
    requestKey !== null && result?.request === identity ? result : null;
  return {
    preview: current?.preview ?? null,
    error: current?.error ?? null,
    loading: requestKey !== null && current === null,
  };
}
